import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    DestroyRef,
    ElementRef,
    OnDestroy,
    computed,
    effect,
    inject,
    input,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import {
    ControlContainer,
    FormControl,
    NgControl,
    ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AncDecimalsOnlyDirective } from '../../directives/anc-decimals-only.directive';
import { AncAlphaNumericOnlyDirective } from '../../directives/anc-alphanumeric-only.directive';
import { AncNumbersOnlyDirective } from '../../directives/anc-numbers-only.directive';
import { AncFieldValidateDirective } from '../../form-validation/anc-field-validate.directive';
import { AncKeepInViewDirective } from '../../directives/anc-keep-in-view.directive';
import { ancShowFieldError } from '../../form-validation/anc-show-field-error.util';

/**
 * Field wrapper with Figma outlined floating label.
 *
 * **Option A — projection (default):** developer supplies `<input>` + optional `[ancError]`.
 *
 * **Option B — declarative:** set `controlName`; lib renders `<input>` internally.
 * **Display row:** `[readonly]="true"` + `[displayValue]` (no `controlName`).
 *
 * @example Option B
 * <lib-anc-form-field [label]="'Product Name (English)'"
 *   controlName="product_name" [readonly]="isReadOnly('product_name_eng')"
 *   [ancFieldValidateSubmitted]="submitted"
 *   [requiredErrorMessage]="'Product name is required'"
 *   [maxlengthErrorMessage]="'Maximum 255 characters are allowed'"
 *   [minlengthErrorMessage]="'Minimum 3 characters are allowed'"></lib-anc-form-field>
 */
@Component({
    selector: 'lib-anc-form-field',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        AncFieldValidateDirective,
        AncNumbersOnlyDirective,
        AncDecimalsOnlyDirective,
        AncAlphaNumericOnlyDirective
    ],
    templateUrl: './anc-form-field.component.html',
    styleUrls: ['./anc-form-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [AncKeepInViewDirective],
    host: {
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut()',
        '(input)': 'updateHasValue()',
        '(change)': 'updateHasValue()'
    }
})
export class AncFormFieldComponent
    implements AfterContentInit, AfterViewInit, OnDestroy
{
    /** Field label text (run through the translate pipe). */
    label = input<string>('');
    /** Renders the required asterisk when true. */
    required = input<boolean>(false);

    /** Reactive-forms control name — when set, lib renders input internally. */
    controlName = input<string>('');
    /** Native input `type` (Option B). */
    type = input<string>('text');
    readonly = input<boolean>(false);
    autocomplete = input<string>('off');
    /**
     * Accessible name for the embedded input. Needed when the field carries no
     * `label` (e.g. a row-scoped colour hex field whose name lives in the row),
     * since the floating label is what normally names the control.
     */
    ariaLabel = input<string>('');

    /** When true, invalid controls show error styling/messages even if pristine. */
    validateSubmitted = input(false, { alias: 'ancFieldValidateSubmitted' });
    /** Group-level error for this control (red border without a control-level error). */
    forceInvalid = input(false, { alias: 'ancFieldForceInvalid' });
    /** Built-in required validation message (no projected `[ancError]` needed). */
    requiredErrorMessage = input<string>('');
    /** Built-in maxlength validation message. */
    maxlengthErrorMessage = input<string>('');
    /** Built-in minlength validation message. */
    minlengthErrorMessage = input<string>('');
    /** Built-in min validation message. */
    minErrorMessage = input<string>('');
    /** Built-in max validation message. */
    maxErrorMessage = input<string>('');
    /** Built-in pattern validation message. */
    patternErrorMessage = input<string>('');
    /** Default pattern message for `[decimalsOnly]` fields (e.g. trailing `.` in `0.`). */
    decimalsPatternErrorMessage = input<string>('Invalid characters found');
    /** Restrict embedded input to digits only (Option B). */
    numbersOnly = input<boolean>(false);
    /** Restrict embedded input to alphanumeric characters (Option B). */
    alphaNumericOnly = input<boolean>(false);
    /** Restrict embedded input to decimal numbers (Option B). */
    decimalsOnly = input<boolean>(false);
    /** Max digits after decimal when `[decimalsOnly]` is true; omit for no limit. */
    decimalsMaxPlaces = input<number | undefined>(undefined);
    /** Static display value with `[readonly]="true"` (no `controlName`). */
    displayValue = input<string | number | null | undefined>(undefined);

    @ContentChild(NgControl) private readonly ngControl?: NgControl;

    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);
    /** Internal control for `[displayValue]` display rows (not part of the parent form). */
    private readonly displayControl = new FormControl({
        value: '',
        disabled: true
    });
    /** Parent `[formGroup]` / `formGroupName` — not visible to inner template bindings. */
    private readonly parentForm = inject(ControlContainer, {
        optional: true,
        skipSelf: true
    });

    protected readonly focused = signal(false);
    protected readonly hasValue = signal(false);
    protected readonly floating = computed(() => {
        if (this.isDisplayRow()) {
            return this.focused() || String(this.displayValue() ?? '') !== '';
        }
        return this.focused() || this.hasValue();
    });

    private readonly syncDisplayValue = effect(() => {
        if (!this.isDisplayRow()) {
            return;
        }
        const value = this.displayValue();
        this.displayControl.setValue(value != null ? String(value) : '', {
            emitEvent: false
        });
        Promise.resolve().then(() => this.updateReadonlyTooltips());
    });

    private observer?: MutationObserver;

    ngAfterContentInit(): void {
        if (this.controlName() || this.isDisplayRow()) {
            return;
        }

        this.watchControl(this.ngControl?.control ?? null);
    }

    ngAfterViewInit(): void {
        this.updateHasValue();
        Promise.resolve().then(() => this.updateHasValue());

        if (this.controlName()) {
            this.watchControl(this.embeddedControl());
        } else if (this.isDisplayRow()) {
            this.watchControl(this.displayControl);
        }

        this.observer = new MutationObserver(() => this.updateHasValue());
        this.observer.observe(this.host.nativeElement, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class', 'value']
        });
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    protected onFocusIn(): void {
        this.focused.set(true);
    }

    protected onFocusOut(): void {
        this.focused.set(false);
        this.updateHasValue();
    }

    protected embeddedControl(): FormControl | null {
        const name = this.controlName();
        if (!name) {
            return null;
        }
        const group = this.parentForm?.control;
        if (!group) {
            return null;
        }
        const control = group.get(name);
        return control instanceof FormControl ? control : null;
    }

    /** `[readonly]` + `[displayValue]` row — not part of the parent reactive form. */
    protected isDisplayRow(): boolean {
        return !this.controlName() && this.displayValue() !== undefined;
    }

    /** Parent form control or internal display control (`displayValue`). */
    protected resolvedControl(): FormControl | null {
        if (this.controlName()) {
            return this.embeddedControl();
        }
        if (this.isDisplayRow()) {
            return this.displayControl;
        }
        return null;
    }

    protected fieldControl(): AbstractControl | null {
        if (this.controlName()) {
            return this.embeddedControl();
        }
        if (this.isDisplayRow()) {
            return null;
        }
        return this.ngControl?.control ?? null;
    }

    protected updateHasValue(): void {
        let filled = false;

        const control = this.fieldControl();
        if (control) {
            const value = control.value;
            filled = value != null && String(value).trim() !== '';
        }

        const root = this.host.nativeElement as HTMLElement;
        if (!filled) {
            const controls = root.querySelectorAll<
                HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            >('input, textarea, select');
            controls.forEach((c) => {
                if (c.value != null && String(c.value).trim() !== '') {
                    filled = true;
                }
            });
        }
        if (!filled && root.querySelector('.ng-has-value, .ng-value')) {
            filled = true;
        }
        this.hasValue.set(filled);
        this.updateReadonlyTooltips();
        this.cdr.markForCheck();
    }

    /** Native title tooltip for read-only values that may truncate with ellipsis. */
    protected readonlyTooltip(): string | null {
        if (!this.readonly() && !this.isDisplayRow()) {
            return null;
        }

        let text = '';
        if (this.isDisplayRow()) {
            const value = this.displayValue();
            text = value != null ? String(value).trim() : '';
        } else {
            const control = this.embeddedControl();
            text = control?.value != null ? String(control.value).trim() : '';
        }

        return text && text !== '-' ? text : null;
    }

    /** Sync title on projected read-only inputs (Option A). */
    private updateReadonlyTooltips(): void {
        const root = this.host.nativeElement as HTMLElement;
        root.querySelectorAll<HTMLInputElement>(
            'input.form-control[readonly]:not(textarea)'
        ).forEach((input) => {
            const text = input.value?.trim() ?? '';
            input.title = text && text !== '-' ? text : '';
        });
    }

    protected showBuiltInFieldError(): boolean {
        return ancShowFieldError(this.fieldControl(), this.validateSubmitted());
    }

    protected hasBuiltInErrorMessages(): boolean {
        return !!(
            this.requiredErrorMessage() ||
            this.maxlengthErrorMessage() ||
            this.minlengthErrorMessage() ||
            this.minErrorMessage() ||
            this.maxErrorMessage() ||
            this.resolvedPatternErrorMessage()
        );
    }

    /** Custom pattern copy, or the generic decimals message when `[decimalsOnly]` is set. */
    protected resolvedPatternErrorMessage(): string {
        if (this.patternErrorMessage()) {
            return this.patternErrorMessage();
        }
        if (this.decimalsOnly()) {
            return this.decimalsPatternErrorMessage();
        }
        return '';
    }

    protected showRequiredError(): boolean {
        return (
            this.showBuiltInFieldError() &&
            !!this.requiredErrorMessage() &&
            !!this.fieldControl()?.errors?.['required']
        );
    }

    protected showMaxlengthError(): boolean {
        return (
            this.showBuiltInFieldError() &&
            !!this.maxlengthErrorMessage() &&
            !!this.fieldControl()?.errors?.['maxlength']
        );
    }

    protected showMinlengthError(): boolean {
        return (
            this.showBuiltInFieldError() &&
            !!this.minlengthErrorMessage() &&
            !!this.fieldControl()?.errors?.['minlength']
        );
    }

    protected showMinError(): boolean {
        return (
            this.showBuiltInFieldError() &&
            !!this.minErrorMessage() &&
            !!this.fieldControl()?.errors?.['min']
        );
    }

    protected showMaxError(): boolean {
        return (
            this.showBuiltInFieldError() &&
            !!this.maxErrorMessage() &&
            !!this.fieldControl()?.errors?.['max']
        );
    }

    protected showPatternError(): boolean {
        return (
            this.showBuiltInFieldError() &&
            !!this.resolvedPatternErrorMessage() &&
            !!this.fieldControl()?.errors?.['pattern']
        );
    }

    private watchControl(control: AbstractControl | null): void {
        if (!control) {
            return;
        }

        this.updateHasValue();

        control.valueChanges
            ?.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                Promise.resolve().then(() => this.updateHasValue());
                this.cdr.markForCheck();
            });
        control.statusChanges
            ?.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.cdr.markForCheck());
    }
}
